import React, { useState, useRef } from "react";
import "./App.css";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const ALL_DAYS = [
  { abbr: "Mon", full: "Monday" },
  { abbr: "Tue", full: "Tuesday" },
  { abbr: "Wed", full: "Wednesday" },
  { abbr: "Thu", full: "Thursday" },
  { abbr: "Fri", full: "Friday" },
  { abbr: "Sat", full: "Saturday" },
  { abbr: "Sun", full: "Sunday" },
];

const DEFAULT_TIMES = [
  "08:00 - 09:00",
  "09:15 - 10:15",
  "10:30 - 11:30",
  "11:45 - 12:45",
  "01:30 - 02:30",
  "02:45 - 03:45",
  "04:00 - 05:00",
  "05:15 - 06:15",
  "06:30 - 07:30",
  "07:45 - 08:45",
];

const SAMPLE_EVENTS = [
  [
    "Math 101\nRoom 302",
    "English Lit\nRoom 104",
    "Math 101\nRoom 302",
    "English Lit\nRoom 104",
    "Calculus Quiz\nOnline",
  ],
  [
    "Physics\nLab 2",
    "History\nRoom 210",
    "Chemistry\nRoom 105",
    "Physics\nLab 2",
    "History\nRoom 210",
  ],
  [
    "Chemistry\nRoom 105",
    "Statistics\nRoom 201",
    "Office Hours\nProf. Evans",
    "Statistics\nRoom 201",
    "Data Science\nLab 3",
  ],
  [
    "Open Study\nLibrary",
    "Team Project\nRoom 102",
    "Seminar\nAuditorium A",
    "Team Project\nRoom 102",
    "Recreation /\nSports",
  ],
  [
    "Web Dev\nLab 1",
    "Algorithms\nLab 4",
    "Library\nQuiet Study",
    "Web Dev\nLab 1",
    "Weekly Review\nPlanning",
  ],
];

export default function App() {
  const [period, setPeriod] = useState(5);
  const [startDay, setStartDay] = useState(0);
  const [sessions, setSessions] = useState(5);
  const [designLayout, setDesignLayout] = useState(true); // true = Edit Mode, false = Preview Mode
  const [isExporting, setIsExporting] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // 10 sessions max x 7 days max 2D array for rock-solid stability
  const [gridData, setGridData] = useState(() =>
    Array.from({ length: 10 }, () => Array(7).fill("")),
  );

  const [timeSlots, setTimeSlots] = useState(() => [...DEFAULT_TIMES]);

  const schedulePrintRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  const showToast = (message) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(message);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage("");
    }, 3200);
  };

  // Compute the current visible day list based on startDay and period
  const visibleDays = Array.from({ length: period }, (_, i) => {
    return ALL_DAYS[(startDay + i) % 7];
  });

  const handlePeriodChange = (newPeriod) => {
    const val = Math.max(1, Math.min(7, Number(newPeriod)));
    setPeriod(val);
  };

  const handleSessionsChange = (newSessions) => {
    const val = Math.max(1, Math.min(10, Number(newSessions)));
    setSessions(val);
  };

  const handleCellChange = (sessionIdx, dayIdx, value) => {
    setGridData((prev) => {
      const next = prev.map((row) => [...row]);
      if (!next[sessionIdx]) next[sessionIdx] = Array(7).fill("");
      next[sessionIdx][dayIdx] = value;
      return next;
    });
  };

  const handleTimeChange = (sessionIdx, value) => {
    setTimeSlots((prev) => {
      const next = [...prev];
      next[sessionIdx] = value;
      return next;
    });
  };

  const handleLoadSample = () => {
    setPeriod(5);
    setStartDay(0);
    setSessions(5);
    setTimeSlots([...DEFAULT_TIMES]);

    setGridData((prev) => {
      const next = Array.from({ length: 10 }, () => Array(7).fill(""));
      for (let s = 0; s < 5; s++) {
        for (let d = 0; d < 5; d++) {
          next[s][d] = SAMPLE_EVENTS[s][d] || "";
        }
      }
      return next;
    });

    showToast("✨ Sample college timetable loaded!");
  };

  const handleReset = () => {
    if (
      window.confirm(
        "Are you sure you want to clear all events in the schedule?",
      )
    ) {
      setGridData(Array.from({ length: 10 }, () => Array(7).fill("")));
      showToast("Schedule cleared.");
    }
  };

  // Export to PNG
  const handleDownloadPng = async () => {
    if (!schedulePrintRef.current) return;
    setIsExporting(true);

    try {
      const canvas = await html2canvas(schedulePrintRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imgData;
      link.download = `schedule-${period}days-${sessions}sessions.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast("🖼️ Schedule exported as high-res PNG!");
    } catch (err) {
      console.error("Export PNG failed:", err);
      alert("Failed to export PNG. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Export to PDF
  const handleDownloadPdf = async () => {
    if (!schedulePrintRef.current) return;
    setIsExporting(true);

    try {
      const canvas = await html2canvas(schedulePrintRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const isLandscape = period >= 4;
      const pdf = new jsPDF({
        orientation: isLandscape ? "landscape" : "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12; // mm
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;

      let imgWidth = maxWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }

      const xPos = margin + (maxWidth - imgWidth) / 2;
      const yPos = margin + (maxHeight - imgHeight) / 2;

      pdf.addImage(imgData, "JPEG", xPos, yPos, imgWidth, imgHeight);
      pdf.save(`schedule-${period}days-${sessions}sessions.pdf`);

      showToast("📄 Schedule exported as PDF document!");
    } catch (err) {
      console.error("Export PDF failed:", err);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="app-container">
      {/* ==================== Top Header Bar ==================== */}
      <header className="app-header">
        <div className="brand-section">
          <div className="brand-icon-badge">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <div className="brand-titles">
            <h1>Scheduler</h1>
            <p>Visual Timetable & Schedule Generator</p>
          </div>
        </div>

        {/* Edit Mode / Preview Mode Switcher */}
        <div className="view-switcher-pill">
          <button
            type="button"
            className={`segmented-btn ${designLayout ? "active" : ""}`}
            onClick={() => setDesignLayout(true)}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Edit Mode
          </button>
          <button
            type="button"
            className={`segmented-btn ${!designLayout ? "active" : ""}`}
            onClick={() => setDesignLayout(false)}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            Preview Mode
          </button>
        </div>

        {/* Header Action Buttons */}
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleLoadSample}
            title="Populate with a ready-to-use sample schedule"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
            Load Sample
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleDownloadPng}
            disabled={isExporting}
            title="Download high-resolution image"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            Export PNG
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleDownloadPdf}
            disabled={isExporting}
            title="Download printable PDF document"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            Export PDF
          </button>
        </div>
      </header>

      {/* ==================== Main Workspace ==================== */}
      <div className="main-workspace">
        {/* Sidebar Controls */}
        <aside className="sidebar-panel">
          <div className="panel-section">
            <div className="section-title">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
              Grid Configuration
            </div>

            {/* Start Day of Week */}
            <div className="control-card">
              <label htmlFor="startDaySelect">
                Start Day
                <span className="badge-hint">Cycle</span>
              </label>
              <select
                id="startDaySelect"
                className="modern-select"
                value={startDay}
                onChange={(e) => setStartDay(Number(e.target.value))}
              >
                <option value={0}>Monday</option>
                <option value={1}>Tuesday</option>
                <option value={2}>Wednesday</option>
                <option value={3}>Thursday</option>
                <option value={4}>Friday</option>
                <option value={5}>Saturday</option>
                <option value={6}>Sunday</option>
              </select>
            </div>

            {/* Period (Days) Stepper */}
            <div className="control-card">
              <label>
                Period (Days)
                <span className="badge-hint">{period} Days</span>
              </label>
              <div className="stepper-group">
                <button
                  type="button"
                  className="stepper-btn"
                  onClick={() => handlePeriodChange(period - 1)}
                  disabled={period <= 1}
                  aria-label="Decrease days"
                >
                  −
                </button>
                <span className="stepper-value">{period}</span>
                <button
                  type="button"
                  className="stepper-btn"
                  onClick={() => handlePeriodChange(period + 1)}
                  disabled={period >= 7}
                  aria-label="Increase days"
                >
                  +
                </button>
              </div>
              <div className="chip-group">
                <button
                  type="button"
                  className="chip-btn"
                  onClick={() => handlePeriodChange(5)}
                >
                  5 Days (Mon-Fri)
                </button>
                <button
                  type="button"
                  className="chip-btn"
                  onClick={() => handlePeriodChange(7)}
                >
                  7 Days (Full)
                </button>
              </div>
            </div>

            {/* Sessions / Time slots Stepper */}
            <div className="control-card">
              <label>
                Number of Sessions
                <span className="badge-hint">{sessions} Slots</span>
              </label>
              <div className="stepper-group">
                <button
                  type="button"
                  className="stepper-btn"
                  onClick={() => handleSessionsChange(sessions - 1)}
                  disabled={sessions <= 1}
                  aria-label="Decrease sessions"
                >
                  −
                </button>
                <span className="stepper-value">{sessions}</span>
                <button
                  type="button"
                  className="stepper-btn"
                  onClick={() => handleSessionsChange(sessions + 1)}
                  disabled={sessions >= 10}
                  aria-label="Increase sessions"
                >
                  +
                </button>
              </div>
              <div className="chip-group">
                <button
                  type="button"
                  className="chip-btn"
                  onClick={() => handleSessionsChange(4)}
                >
                  4 Slots
                </button>
                <button
                  type="button"
                  className="chip-btn"
                  onClick={() => handleSessionsChange(5)}
                >
                  5 Slots
                </button>
                <button
                  type="button"
                  className="chip-btn"
                  onClick={() => handleSessionsChange(6)}
                >
                  6 Slots
                </button>
              </div>
            </div>
          </div>

          <div className="panel-section">
            <div className="section-title">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              Quick Guide
            </div>

            <div className="info-card">
              <p>
                <strong>Edit Mode:</strong> Click any cell or time slot to type
                subjects, room numbers, or notes.
              </p>
              <p style={{ marginTop: "8px" }}>
                <strong>Preview Mode:</strong> Displays clean presentation
                cards, ready for PDF or PNG printing.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-danger-ghost"
              onClick={handleReset}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Clear All Events
            </button>
          </div>
        </aside>

        {/* Canvas / Schedule Table Area */}
        <main className="canvas-workspace">
          {period === 0 || sessions === 0 ? (
            <div className="empty-state">
              <div className="empty-icon-circle">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              <h2>No Schedule Set</h2>
              <p>
                Adjust the period and session count on the left, or load a
                sample template to start building your timetable.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleLoadSample}
              >
                Load Sample Schedule
              </button>
            </div>
          ) : (
            <div
              id="toPrint"
              ref={schedulePrintRef}
              className="schedule-export-wrapper"
              style={{ width: `${Math.max(640, period * 150 + 150)}px` }}
            >
              {/* Timetable Card Header Banner */}
              <div className="schedule-banner">
                <div className="schedule-banner-title">
                  <h2>Weekly Schedule</h2>
                  <p>
                    Organized timetable for {visibleDays[0]?.full} through{" "}
                    {visibleDays[visibleDays.length - 1]?.full}
                  </p>
                </div>
                <div className="schedule-meta-badge">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  {period} Days • {sessions} Sessions
                </div>
              </div>

              {/* Timetable Table Grid */}
              <table className="timetable-grid">
                <thead>
                  <tr>
                    <th className="timetable-header-cell time-col-header">
                      <div className="day-chip">
                        <span className="day-abbr">Time</span>
                        <span className="day-full">Slot</span>
                      </div>
                    </th>
                    {visibleDays.map((day, idx) => (
                      <th key={idx} className="timetable-header-cell">
                        <div className="day-chip">
                          <span className="day-abbr">{day.abbr}</span>
                          <span className="day-full">{day.full}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: sessions }).map((_, sIdx) => (
                    <tr key={sIdx} className="timetable-row">
                      {/* Left Time Column */}
                      <td className="time-slot-cell">
                        {designLayout ? (
                          <textarea
                            className="time-input"
                            value={timeSlots[sIdx] || ""}
                            onChange={(e) =>
                              handleTimeChange(sIdx, e.target.value)
                            }
                            placeholder="e.g. 09:00 - 10:00"
                            rows={2}
                          />
                        ) : (
                          <div className="time-badge">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            <span>
                              {timeSlots[sIdx] || `Session ${sIdx + 1}`}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Day Event Cells */}
                      {Array.from({ length: period }).map((_, dIdx) => {
                        const cellValue = gridData[sIdx]?.[dIdx] || "";
                        return (
                          <td key={dIdx} className="timetable-cell">
                            {designLayout ? (
                              <textarea
                                className="cell-textarea"
                                value={cellValue}
                                onChange={(e) =>
                                  handleCellChange(sIdx, dIdx, e.target.value)
                                }
                                placeholder="Add event / class..."
                              />
                            ) : (
                              <div
                                className={`event-card ${!cellValue.trim() ? "empty" : ""}`}
                              >
                                <div className="event-text">{cellValue}</div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {/* ==================== Notification Toast ==================== */}
      {toastMessage && (
        <div className="toast-notice">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#10b981"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          {toastMessage}
        </div>
      )}
    </div>
  );
}
